import { useMutation } from '@tanstack/react-query';
import {
  signIn,
  signInWithRedirect,
  confirmSignIn,
  confirmUserAttribute,
  resetPassword,
  confirmResetPassword,
  sendUserAttributeVerificationCode,
  updateMFAPreference,
  updateUserAttribute,
} from 'aws-amplify/auth';
import { trpc, queryClient } from '@/utils/trpc';

/** Fetches user info (email, extAuthProvider, enableMfa) for the login flow. Call when user clicks Continue. */
export const useFetchUserInfo = () => {
  return useMutation({
    mutationFn: async (email: string) => {
      return queryClient.fetchQuery(
        trpc.userInfoRouter.getUserInfo.queryOptions({ email }),
      );
    },
  });
};

/** Triggers Cognito SSO redirect using the provider name returned by the API. */
export const signInWithSsoRedirect = (extAuthProvider: string): Promise<void> =>
  signInWithRedirect({ provider: { custom: extAuthProvider } });

export const useSignIn = () => {
  return useMutation({
    mutationFn: async (data: { email: string; password: string }) => {
      try {
        const result = await signIn({
          username: data.email,
          password: data.password,
        });
        return {
          success: true,
          nextStep: result.nextStep,
        };
      } catch (error: unknown) {
        throw new Error(
          error instanceof Error ? error.message : 'Failed to sign in',
        );
      }
    },
  });
};

export const useConfirmSignInMfa = () => {
  return useMutation({
    mutationFn: async (data: { code: string }) => {
      try {
        await confirmSignIn({
          challengeResponse: data.code,
        });
        return {
          success: true,
          message: 'Phone number successfully verified!',
        };
      } catch (error: unknown) {
        throw new Error(
          error instanceof Error ? error.message : 'Failed to verify MFA code',
        );
      }
    },
  });
};

export const useConfirmSignIn = () => {
  return useMutation({
    mutationFn: async (data: { password: string }) => {
      try {
        const result = await confirmSignIn({
          challengeResponse: data.password,
        });
        return {
          success: true,
          message: 'Password was set successfully.',
          nextStep: result.nextStep,
          isSignedIn: result.isSignedIn,
        };
      } catch (error: unknown) {
        throw new Error(
          error instanceof Error ? error.message : 'Failed to set new password',
        );
      }
    },
  });
};

export const useSetUpSmsMfaPhone = () => {
  return useMutation({
    mutationFn: async (data: { phoneNumber: string }) => {
      try {
        const result = await updateUserAttribute({
          userAttribute: {
            attributeKey: 'phone_number',
            value: data.phoneNumber,
          },
        });

        if (result.nextStep.updateAttributeStep === 'DONE') {
          await sendUserAttributeVerificationCode({
            userAttributeKey: 'phone_number',
          });
        }

        return result;
      } catch (error: unknown) {
        throw new Error(
          error instanceof Error
            ? error.message
            : 'Failed to update phone number',
        );
      }
    },
  });
};

export const useConfirmPhoneNumberSetup = () => {
  return useMutation({
    mutationFn: async (data: { code: string }) => {
      try {
        await confirmUserAttribute({
          userAttributeKey: 'phone_number',
          confirmationCode: data.code,
        });

        await updateMFAPreference({
          sms: 'PREFERRED',
        });

        return {
          success: true,
          message: 'Phone number successfully verified!',
        };
      } catch (error: unknown) {
        throw new Error(
          error instanceof Error
            ? error.message
            : 'Failed to verify phone number',
        );
      }
    },
  });
};

export const useResetPassword = () => {
  return useMutation({
    mutationFn: async (data: { email: string }) => {
      try {
        await resetPassword({ username: data.email });
        return {
          success: true,
          message: 'Password reset link was sent to your email.',
        };
      } catch (error: unknown) {
        throw new Error(
          error instanceof Error
            ? error.message
            : 'Failed to send password reset link.',
        );
      }
    },
  });
};

export const useConfirmResetPassword = () => {
  return useMutation({
    mutationFn: async (data: {
      email: string;
      code: string;
      password: string;
    }) => {
      try {
        await confirmResetPassword({
          username: data.email,
          confirmationCode: data.code,
          newPassword: data.password,
        });
        return {
          success: true,
          message: 'Password was reset.',
        };
      } catch (error: unknown) {
        throw new Error(
          error instanceof Error ? error.message : 'Failed to reset password',
        );
      }
    },
  });
};
